import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, OnInit } from '@angular/core';
import { ComponentBase } from 'src/app/main/dynamic/component-base';
import { AttributeModel } from 'src/app/main/dynamic/model/attribute.model';
import { Subscription } from 'rxjs';
import { BindService } from 'src/app/service/bind.service';
import { SharedDataService } from 'src/app/service/shared-data.service';
import { DynamicEvent } from 'src/app/main/dynamic/dynamic.event';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  animations: [
    trigger('fadeAnimation', [
      state('in', style({opacity: 1})),
      transition(':enter', [
        style({opacity: 0}),
        animate(400)
      ]),
      transition(':leave',
        animate(200, style({opacity: 0})))
    ])
  ]
})
export class SelectComponent extends ComponentBase implements OnInit {

  changingSubscription: Subscription;
  bindingSubscription: Subscription;

  constructor(private bindService: BindService, private sharedDataService: SharedDataService) {
    super()

    this.changingSubscription = this.sharedDataService
      .getNotification()
      .subscribe(async hasChanged => {
        if (hasChanged) {
          await this.loadFromBinding()
        }
      });

    this.bindingSubscription = this.bindService
      .getBindingRequestSubject()
      .subscribe(async attribute => {
        await this.bindFromSourceKey(attribute)
      })
  }

  async ngOnInit() {
    await this.loadFromBinding();

    this.requestBinding();

    if(!this.attribute.value) {
      this.attribute.value = null
    }
  }

  private async bindFromSourceKey(attribute: AttributeModel) {
    let bindingAttr = this.attribute.attributes
      .find(attr => attr.type.toLowerCase() == "binding")

    let commingContext = attribute.options.find(opt => 
      opt.key.toLowerCase() == "BindingContext".toLowerCase())

    let thisContext = this.envOptions.find(opt => 
      opt.key.toLowerCase() == "BindingContext".toLowerCase())

    if(bindingAttr && attribute.value && commingContext.value == thisContext.value) {
      let sourceOpt = bindingAttr.options
        .find(opt => opt.key.toLowerCase() == "sourcekey")

      if(sourceOpt && sourceOpt.value.toLowerCase() == attribute.key.toLowerCase()) {
        let bindingPath = bindingAttr.attributes
          .find(attr => attr.key.toLowerCase() == "bindingpath")

        if(bindingPath) {
          bindingPath.value = attribute.uid
          await this.loadFromBinding();
        }
      }
    }
  }

  private async loadFromBinding() {
    let binding = this.attribute.attributes
      .find(attr => attr.type.toLowerCase() == "binding");

    if (binding) {
      let previousValueIndex = -1
      if(this.attribute.value) {
        previousValueIndex = this.attribute.attributes
          .findIndex(attr => attr.key == this.attribute.value)
      }

      await this.bindService.load(binding);
      this.attribute.attributes = [binding].concat(binding.attributes);

      let containsPreviousValue = this.attribute.attributes
        .some(attr => attr.key == this.attribute.value)

      if(previousValueIndex >= 0 && !containsPreviousValue && 
        this.attribute.attributes.length > previousValueIndex) {
          this.attribute.value = this.attribute.attributes[previousValueIndex].key
      }
    }
  }

  private requestBinding() {
    let selectedOptionAttr = this.attribute.attributes
      .find(attr => attr.key == this.attribute.value);

    let allowRequest = this.attribute.options.some(opt => 
      opt.key.toLowerCase() == "AllowBindingRequest".toLowerCase() && 
      opt.value.toLowerCase() == "true")

    if (selectedOptionAttr && allowRequest) {
      this.bindService.requestBinding({
        uid: selectedOptionAttr.uid,
        key: this.attribute.key,
        value: this.attribute.value,
        type: this.attribute.type,
        options: this.envOptions
      });
    }
  }

  changedSelection() {
    let events: DynamicEvent[] = []
    
    this.validate(events)

    let selectedOptionAttr = this.attribute.attributes
      .find(attr => attr.value.toLowerCase() == this.attribute.value.toLowerCase())
    
    this.addOrRemoveOption(selectedOptionAttr);

    this.requestBinding()

    events.push({ attribute: selectedOptionAttr })
    events.push({ name: "autosave", data: "silently" })

    this.emitEvents(events)
  }

  private addOrRemoveOption(selectedOptionAttr: AttributeModel) {
    if (selectedOptionAttr) {
      let selected = this.attribute.attributes
        .find(attr => attr.type.toLowerCase() == "selectedoption");

      if (!selected) {
        selected = {
          key: `${this.attribute.key} Selected`,
          value: selectedOptionAttr.value,
          type: "SelectedOption",
          options: []
        };

        this.attribute.attributes.push(selected);
      } else {
        selected.value = selectedOptionAttr.value
      }

      selected.attributes = [selectedOptionAttr];
    } else {
      let index = this.attribute.attributes
        .findIndex(attr => attr.type.toLowerCase() == 'selectedoption');

      if (index != -1) {
        this.attribute.attributes.splice(index, 1);
      }
    }
  }

  private validate(events: DynamicEvent[]) {
    if (this.attribute.hasError && this.attribute.value) {
      this.attribute.hasError = false;
      events.push({ name: "validate" });
    }
  }

  getOptionAttributes(): AttributeModel[] {
    return this.attribute.attributes.filter(attr => {
      let showOpt = attr.options.find(opt => opt.key.toLowerCase() == "show" &&
                                             opt.type.toLowerCase() == "boolean")

      if(showOpt) {
        return showOpt.value.toLowerCase() == "true"
      } else if(attr.type.toLowerCase() == "binding" ||
                attr.key.toLowerCase() == "bindingpath" ||
                attr.type.toLowerCase() == "selectedoption") {
        return false
      }

      return true
    })
  }
}
